/**
 * Normalizes text for case- and accent-insensitive search comparisons.
 *
 * Strips diacritics via Unicode NFD decomposition + removal of combining marks
 * (the `Diacritic` Unicode property), then lowercases. Apply to BOTH the search term
 * and the field being matched — folding only one side (e.g. the typed query) while
 * comparing against the other side as-is will not match.
 *
 * This is a client-side-only fold: it cannot make a server-side search endpoint
 * accent-insensitive by itself. If the field being filtered was fetched from an API
 * rather than already held in memory, the backend must fold accents on its side too
 * (see `be-resource-server`'s `QueryUtils.foldedLike` / GIV-5506 / GIV-5507) or this
 * will silently return worse results, not better ones.
 *
 * @param text - the raw text to normalize
 * @returns lowercase, diacritic-stripped text safe to compare with `.includes()`
 *
 * @example
 * ```typescript
 * const term = normalizeSearchText(this.searchQuery());
 * return items.filter(item => normalizeSearchText(item.name).includes(term));
 * ```
 */
export function normalizeSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
